/* eslint-disable complexity */
import { useState, useEffect } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Row } from "@/libraries/wpl/foundations/src";

const easeOutQuad = (t: number) => t * (2 - t);
const frameDuration = 1000 / 60;

const AnimateCount = ({ children = "0", duration = 2000 }) => {
    const countTo = parseInt(children, 10);
    const [count, setCount] = useState(0);

    useEffect(() => {
        let frame = 0;
        const totalFrames = Math.round(duration / frameDuration);
        const counter = setInterval(() => {
            frame++;
            const progress = easeOutQuad(frame / totalFrames);
            setCount(countTo * progress);

            if (frame === totalFrames) {
                clearInterval(counter);
            }
        }, frameDuration);
    }, [countTo, duration]);

    return Math.floor(count)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const TeamStats = () => {
    const intl = useIntl();
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    return (
        <UFOSegment name="homepage-section-stats">
            <section>
                <div css={StatsStyles} ref={scrollRef}>
                    <div className="bgshape-green" />
                    <div className="bgshape-purple" />
                    <Row>
                        <div className="content-wrapper">
                            <div className="earth-contents">
                                <img
                                    className="earth"
                                    src="https://wac-cdn.atlassian.com/misc-assets/webp-images/earf-high-res.webp"
                                    alt={intl.formatMessage({
                                        id: "HomePageTemplateV2.teamStatsPlanetAltTxt",
                                        defaultMessage: "planet Earth"
                                    })}
                                />
                                <div className="globe-circle globe1 globe-border" />
                                <div className="globe-circle globe1 globe-delay-1" />
                                <div className="globe-circle globe1 globe-delay-2" />
                                <div className="globe-circle globe1 globe-delay-3" />
                                <div className="globe-circle globe1 globe-delay-4" />

                                <div className="globe-circle globe2 globe-border" />
                                <div className="globe-circle globe2 globe-delay-1" />
                                <div className="globe-circle globe2 globe-delay-2" />
                                <div className="globe-circle globe2 globe-delay-3" />
                                <div className="globe-circle globe2 globe-delay-4" />

                                <svg
                                    className="blob"
                                    width="107"
                                    height="99"
                                    viewBox="0 0 107 99"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g>
                                        <path
                                            d="M14.0114 15.9997C-4.48828 31 -0.655274 58.4997 4.01139 70.9997C9.51141 82.4999 24.0114 100.087 56.0114 97.9999C79.0114 96.4999 100.511 79 105.011 60C109.511 41 99.5114 15.9997 83.5114 5.99973C67.5114 -4.00027 33.8146 -0.0574808 14.0114 15.9997Z"
                                            fill="#FFA900"
                                        />
                                    </g>
                                </svg>
                            </div>

                            <div className="forty">
                                <h2>
                                    {intl.formatMessage({
                                        id: "HomePageTemplateV2.teamStatsHeader",
                                        defaultMessage:
                                            "Teams across the globe run on Atlassian"
                                    })}
                                </h2>

                                <h3 className="big-stat">
                                    <span className="big-stat">
                                        {sectionIsVisible ? (
                                            <AnimateCount>300000</AnimateCount>
                                        ) : (
                                            ""
                                        )}
                                    </span>
                                    <span>+</span>
                                </h3>
                                <p>
                                    {intl.formatMessage({
                                        id: "HomePageTemplateV2.teamStatsCompaniesTxt",
                                        defaultMessage:
                                            "companies power team collaboration with Atlassian"
                                    })}
                                </p>
                            </div>
                            <div className="twenty">
                                <div className="stat-wrapper">
                                    <h3 className="little-stat">
                                        <span>
                                            {sectionIsVisible ? (
                                                <AnimateCount>200</AnimateCount>
                                            ) : (
                                                ""
                                            )}
                                        </span>
                                    </h3>
                                    <p>
                                        {intl.formatMessage({
                                            id: "HomePageTemplateV2.teamStatsPeopleTxt",
                                            defaultMessage:
                                                "countries have companies that use Atlassian"
                                        })}
                                    </p>
                                </div>
                                <div className="stat-wrapper">
                                    <h3 className="little-stat">
                                        <span>
                                            {sectionIsVisible ? (
                                                <AnimateCount>75</AnimateCount>
                                            ) : (
                                                ""
                                            )}
                                        </span>
                                        <span className="color">%</span>
                                    </h3>
                                    <p>
                                        {intl.formatMessage({
                                            id: "HomePageTemplateV2.teamStatsFortune500Txt",
                                            defaultMessage:
                                                "of Fortune 500 companies use Atlassian products"
                                        })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Row>
                </div>
            </section>
        </UFOSegment>
    );
};

const StatsStyles = css({
    "@keyframes globeturn": {
        "0%": {
            transform: "rotate(0deg)"
        },
        "100%": {
            transform: "rotate(360deg)"
        }
    },
    "@keyframes blobmove": {
        "0%": {
            transform: "translateY(0) translateX(0) rotate(30deg)"
        },
        "25%": {
            transform: "translateY(20px) translateX(10px) rotate(-30deg)"
        },
        "50%": {
            transform: "translateY(0) translateX(-10px) rotate(30deg)"
        },
        "75%": {
            transform: "translateY(-20px) translateX(10px) rotate(-30deg)"
        },
        "100%": {
            transform: "translateY(0) translateX(0)"
        }
    },
    "max-width": "1440px",
    "margin": "0 auto",
    "position": "relative",
    "zIndex": 1,
    "padding": "100px 0",
    "overflow": "hidden",
    "background": "#F5F5F6",
    "clipPath":
        "polygon(20px 10%,calc(100% - 32px) 0,calc(100% - 43px) 92%,50% 100%,50% 90%,40px 90%)",
    ".bgshape-green": {
        background: "#82B536",
        clipPath: "polygon(0 0,100% 0,110% 52%,0 80%)",
        position: "absolute",
        width: "35%",
        height: "10%",
        insetInlineEnd: 0,
        insetBlockStart: 0,
        insetBlockEnd: 0
    },
    ".bgshape-purple": {
        background: "#BF63F3",
        clipPath: "polygon(0 17%,97% 0,100% 55%,0 53%)",
        position: "absolute",
        width: "22%",
        height: "10%",
        insetInlineEnd: 0,
        insetBlockStart: "26px",
        insetBlockEnd: 0,
        mixBlendMode: "normal"
    },

    ".earth-contents": {
        "width": "56%",
        "aspectRatio": "1/1",
        "position": "absolute",
        "insetInlineEnd": "0",
        "insetBlockEnd": "0",
        [media.above.sm]: {
            transform: "translate(40%, 68%)"
        },
        ".globe-circle": {
            "position": "absolute",
            "width": "100%",
            "aspectRatio": "1/1",
            "borderRadius": "50%",
            "animation": "globeturn linear 30s infinite",

            "&.globe-border": {
                border: "1px solid #999"
            },
            "&.globe-delay-1": {
                animationDelay: "-8s"
            },
            "&.globe-delay-2": {
                animationDelay: "-6s"
            },
            "&.globe-delay-3": {
                animationDelay: "-4s"
            },
            "&.globe-delay-4": {
                animationDelay: "-2s"
            },
            "&.globe1": {
                insetInlineEnd: "10%"
            },
            "&.globe2": {
                insetBlockEnd: "10%",
                insetInlineEnd: "-5%"
            },
            "&:before, &:after": {
                content: "",
                position: "absolute",
                background: "#82B536",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                insetInlineStart: 0,
                insetInlineEnd: 0,
                margin: "auto",
                zIndex: 88
            },
            "&:before": {
                insetBlockStart: "-4px"
            },
            "&:after": {
                insetBlockEnd: "-4px"
            }
        },
        ".earth": {
            animation: "globeturn linear 60s infinite",
            position: "absolute",
            width: "100%"
        },
        ".blob": {
            position: "absolute",
            insetInlineEnd: "19%",
            insetBlockStart: "1%",
            animation: "blobmove alternate 10s infinite",
            mixBlendMode: "multiply"
        }
    },

    ".content-wrapper": {
        "maxWidth": "100%",
        "display": "flex",
        "alignItems": "flex-start",
        "gap": "40px",
        "position": "relative",
        "@media (max-width: 768px)": {
            "flexDirection": "column",
            "padding": "70px 80px 200px",
            ".earth-contents": {
                inset: "80% 0 auto 0",
                margin: "auto",
                width: "80%"
            }
        },
        "h2": {
            fontSize: "2.5rem",
            lineHeight: 1.2,
            margin: 0
        },
        "h3": {
            "lineHeight": 1,
            "margin": 0,
            "position": "relative",
            "color": "#1868DB",
            "&.big-stat": {
                fontSize: "5rem",
                fontWeight: 700
            },
            "&.little-stat": {
                fontWeight: 900,
                fontSize: "3rem"
            }
        },

        "p": {
            fontSize: "1rem",
            margin: "10px 0 40px"
        },
        ".forty": {
            flexBasis: "40%"
        },

        ".twenty": {
            flexBasis: "20%"
        },
        ".stat-wrapper": {
            "position": "relative",

            "&:before": {
                content: "",
                position: "absolute",
                height: "100%",
                background: "#101214",
                width: "3px",
                insetInlineStart: "-20px"
            }
        }
    }
});
